/* component-companion-gift.js — "Gratis Matratzenschoner zum Premium Topper" companion gift (ES module, every page).

   RULE: every Premium Komfort Topper unit in the cart earns ONE Matratzenschoner of the SAME size (width x length)
   for free. 2 toppers 140x200 -> 2 protectors 140x200. No per-order cap. The automatic Function discount makes the
   gift lines 0 €; this module only keeps the CART CONTENT in parity with that rule.

   CONFIG (layout renders <script type="application/json" data-kk-companion>):
     { buy: [product ids], buyVariants: { "<topper variant id>": "140x200" },
       gift: { product: <id>, variants: { "140x200": { id: <variant id>, available: true } } },
       start: "YYYY-MM-DD HH:MM" (optional), end: "YYYY-MM-DD HH:MM", tz: "Europe/Berlin", perUnit: 1, cartUrl: "/cart" }
     Missing/invalid config, a `start` still in the future or an `end` already in the past (parsed in `tz`) -> nothing runs.

   CART CONTRACT (window.KK.cart from global.js): add(item, {sections}) / change(lineKey, qty, {sections}) -> Promise<cart>;
   every mutation publishes 'kk:cart:updated' { cart, added?, changed?: {key, quantity, previous}, updated?: {updates} }.
   Gift lines carry the line property _gift = "true". A protector bought normally (no flag) also counts as the free one
   when a matching topper is present (parity with earlier promos), so we never add a second one on top of it.

   planReconcile(cart, cfg, declined) is PURE (exported, unit-tested in tests/companion-gift.test.mjs):
     -> [{ op:'add', id, quantity, properties:{_gift:'true'}, size }, { op:'change', key, quantity, size }]
     need[size]  = topper units * perUnit;  normal[size] = un-flagged protector qty;  want = max(0, need - normal)
     cur < want  -> one 'add' of the difference (skipped for declined / unavailable / unknown sizes)
     cur > want  -> first gift line := want, every further gift line of that size := 0 (declined sizes still trimmed)

   RUNTIME: initial cart = KK.cart.state / KK.cart.get(); then every kk:cart:updated snapshot:
     · a gift line the CUSTOMER changed to fewer units / removed -> its size is DECLINED (sessionStorage
       kk_companion_declined); our own trims are recognised by their line keys and never count as a decline
     · a buy product was added -> its size is un-declined (the customer wants the topper again)
     · then plan + apply ops SEQUENTIALLY (adds first, then changes) with { sections:[cart-drawer section id] } so the
       drawer re-renders. The PAGE-LOAD pass is SILENT (plain /cart/add.js + KK.cart.reconcile()) so a returning
       visitor's drawer does not pop open uninvited (only an `added` payload opens the drawer).
     · echoes of our own mutations are skipped, max 3 passes per trigger, a size whose add was rejected is not retried on
       this page view, 3 consecutive failures stop the module for this page view. No KK.cart -> plain endpoints + reload.
     · the cart PAGE re-renders itself from kk:cart:updated (sections/main-cart.liquid) - no reload needed there.

   PDP CARD: [data-companion-card data-map='{ topper:{"<variant id>":"140x200"}, sizes:{"140x200":{id,size_fmt,uvp_fmt,
   price_fmt,available}} }'] > [data-companion-line data-tpl="Matratzenschoner {size} · UVP {uvp} → 0,00 €" data-soldout="…"].
   Re-rendered for the current topper variant on load and on kk:variant:changed (payload.variant.id, optionally filtered by
   the card's data-section-id); DOM is built with createElement/textContent only: {size} -> <span class="companion-line__name">,
   {uvp} -> <s>, {price} (or the text after the last "→"/"->") -> <b>. Unknown/unavailable size -> data-soldout + .is-soldout.
   Touches window ONLY inside init() (guarded), so node can import planReconcile. No external imports.

   ANY-SIZE MODE (2026-09-15, Gratis Wolkenkissen ab 139 €, cfg.sizeMatch === false): ONE unit (cfg.maxPerOrder) of ANY single
   variant of the gift product is free when a buy product is in the cart AND the cart subtotal WITHOUT the unit the Function
   makes free (first flagged line, else the customer's cheapest own gift line) reaches cfg.minSubtotal (major units; sums
   original_line_price = before discounts, like the Function's subtotalAmount). Variants whose title contains cfg.exclude
   ("2er Set") never count. gift.products = extra product ids (ad-LP clones) that also count as the gift. The variant to add =
   cfg.chosen (PDP card select [data-companion-size], sessionStorage kk_companion_variant) else cfg.defaultVariant; a new pick
   swaps the auto-added line (add chosen, trim the old flagged lines). Decline key is '*'. ?kk_companion=now in the URL stores
   sessionStorage kk_companion_force = 1 which skips the `start` gate (preview verification before the promo starts). */

const isGiftLine = (it) => !!(it && it.properties && it.properties._gift);
const num = (v) => Number(v);

// "YYYY-MM-DD HH:MM" in an IANA tz -> epoch ms (same logic as kk-countdown.js; DST-safe via Intl, two passes).
function tzOffsetMs(date, tz) {
  const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const p = {};
  f.formatToParts(date).forEach((x) => { p[x.type] = x.value; });
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - date.getTime();
}
export function parseInTz(str, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(str || '').trim());
  if (!m) return NaN;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], 0);
  let off = tzOffsetMs(new Date(guess), tz);
  off = tzOffsetMs(new Date(guess - off), tz);
  return guess - off;
}

// size key ("140x200") of a gift-product variant id, from cfg.gift.variants
export function giftSizeOf(cfg, variantId) {
  const vs = (cfg && cfg.gift && cfg.gift.variants) || {};
  return Object.keys(vs).find((size) => vs[size] && String(vs[size].id) === String(variantId)) || null;
}

// Gift size that serves a topper size: the exact size when available, else cfg.fallbacks[size] when that one is available
// (Jonas 2026-09-11: 80 x 200 stock is low -> {"80x200":"90x200"}). Returns { size, fallback:boolean } or null.
export function resolveGiftSize(cfg, size) {
  const vs = (cfg && cfg.gift && cfg.gift.variants) || {};
  if (vs[size] && vs[size].available && vs[size].id != null) return { size, fallback: false };
  const alt = (cfg.fallbacks || {})[size];
  if (alt && vs[alt] && vs[alt].available && vs[alt].id != null) return { size: alt, fallback: true, original: size };
  return null;
}

export function planAnySize(cart, cfg, declined) {
  const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
  const buy = new Set((cfg.buy || []).map(num));
  const giftSet = new Set([num(cfg.gift && cfg.gift.product)].concat(((cfg.gift && cfg.gift.products) || []).map(num)));
  const all = (cfg.gift && cfg.gift.all) || {};
  const excl = String(cfg.exclude || '').trim().toLowerCase();
  const exIds = new Set((cfg.excludeIds || []).map(num));
  const perUnit = Math.max(0, num(cfg.perUnit == null ? 1 : cfg.perUnit) || 0);
  const cap = num(cfg.maxPerOrder) > 0 ? num(cfg.maxPerOrder) : Infinity;
  const fxr = (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1;   // 2026-09-18: minSubtotal is EUR, cart amounts are presentment (CZK)
  // PLN-THRESH-2026-10-20: cfg.minLocalCents > 0 = round local threshold already in cart-currency cents (PLN 600 zl) - no rate.
  const min = num(cfg.minLocalCents) > 0 ? Math.round(num(cfg.minLocalCents)) : Math.round(Math.max(0, num(cfg.minSubtotal) || 0) * 100 * fxr);
  const decl = declined instanceof Set ? declined : new Set(declined || []);
  const unit = (it) => num(it.original_price) || (num(it.quantity) > 0 ? (num(it.original_line_price) || 0) / num(it.quantity) : 0);
  let units = 0, subtotal = 0;
  const flagged = [], normals = [];
  items.forEach((it) => {
    const pid = num(it.product_id);
    const q = num(it.quantity) || 0;
    subtotal += num(it.original_line_price) || unit(it) * q;
    if (buy.has(pid)) units += q;
    else if (giftSet.has(pid)) {
      const t = String(it.variant_title || it.title || '').toLowerCase();
      if ((excl && t.indexOf(excl) !== -1) || exIds.has(num(it.variant_id || it.id))) return;   // 2026-09-18: titles are translated ('2-pack') - ids are not
      if (isGiftLine(it)) flagged.push(it); else normals.push(it);
    }
  });
  normals.sort((a, b) => unit(a) - unit(b));
  const normal = normals.reduce((n, it) => n + (num(it.quantity) || 0), 0);
  const cur = flagged.reduce((n, it) => n + (num(it.quantity) || 0), 0);
  const freeLine = flagged[0] || normals[0] || null;       // the unit the Function makes free does not count towards the threshold
  if (cfg.anyOrder === true && units === 0) units = 1;   // 2026-09-28: Gratis Schlafspray ab 139 € – every order, no buy product
  const qualifies = units > 0 && (subtotal - (freeLine ? unit(freeLine) : 0)) >= min;
  const want = qualifies ? Math.max(0, Math.min(units * perUnit, cap) - normal) : 0;
  const chosen = String(cfg.chosen || cfg.defaultVariant || '');
  const cv = all[chosen];
  const canAdd = !decl.has('*') && !!(cv && cv.available) && cfg.optIn === true;   // 2026-09-16: opt-in only (PDP checkbox / cart gift bar)
  const ops = [];
  if (cur < want) {
    if (canAdd) ops.push({ op: 'add', id: num(chosen), quantity: want - cur, properties: { _gift: 'true' }, size: '*' });
  } else if (cur > want) {
    flagged.forEach((it, i) => { const q = i === 0 ? want : 0; if (num(it.quantity) !== q) ops.push({ op: 'change', key: it.key, quantity: q, size: '*' }); });
  } else if (want > 0 && canAdd && !flagged.some((it) => String(it.id) === chosen)) {
    // the customer picked another size on the PDP card: add the chosen one first, then drop the old auto-added line(s)
    ops.push({ op: 'add', id: num(chosen), quantity: want, properties: { _gift: 'true' }, size: '*' });
    flagged.forEach((it) => ops.push({ op: 'change', key: it.key, quantity: 0, size: '*' }));
  }
  return ops;
}

export function planReconcile(cart, cfg, declined) {
  if (cfg && cfg.sizeMatch === false) return planAnySize(cart, cfg, declined);
  const items = (cart && Array.isArray(cart.items)) ? cart.items : [];
  const buy = new Set((cfg.buy || []).map(num));
  const giftProduct = num(cfg.gift && cfg.gift.product);
  const perUnit = Math.max(0, num(cfg.perUnit == null ? 1 : cfg.perUnit) || 0);
  const decl = declined instanceof Set ? declined : new Set(declined || []);
  const need = {}, normal = {}, giftLines = {}, viaFallback = {};
  items.forEach((it) => {
    const pid = num(it.product_id);
    if (buy.has(pid)) {
      const size = (cfg.buyVariants || {})[String(it.id)];
      if (!size) return;
      const res = resolveGiftSize(cfg, size);
      const key = res ? res.size : size;          // unresolvable sizes keep their key: nothing is added, extras are still trimmed
      need[key] = (need[key] || 0) + (num(it.quantity) || 0) * perUnit;
      if (res && res.fallback) viaFallback[key] = res.original;
    } else if (pid === giftProduct) {
      const size = giftSizeOf(cfg, it.id);
      if (!size) return;
      if (isGiftLine(it)) (giftLines[size] || (giftLines[size] = [])).push(it);
      else normal[size] = (normal[size] || 0) + (num(it.quantity) || 0);
    }
  });
  const adds = [], changes = [];
  new Set([...Object.keys(need), ...Object.keys(giftLines)]).forEach((size) => {
    const want = Math.max(0, (need[size] || 0) - (normal[size] || 0));
    const lines = giftLines[size] || [];
    const cur = lines.reduce((n, it) => n + (num(it.quantity) || 0), 0);
    const v = cfg.gift.variants[size];
    if (cur < want) {
      if (!decl.has(size) && v && v.available && v.id != null) {
        const props = { _gift: 'true' };
        if (cfg.setFlag) props._set = String(cfg.setFlag);   // v4.41: promo 3 flag (e.g. schoner-1215) -> Flow order tag set-<flag>
        // substitute size: a visible line property tells the customer (and the packer) why the size differs
        if (viaFallback[size] && cfg.note) {
          const fmt = (k) => (cfg.gift.variants[k] && cfg.gift.variants[k].title) || k.replace('x', ' x ') + ' cm';
          props[cfg.noteKey || 'Hinweis'] = String(cfg.note).replace(/\{size\}/g, fmt(viaFallback[size])).replace(/\{alt\}/g, fmt(size));
        }
        adds.push({ op: 'add', id: num(v.id), quantity: want - cur, properties: props, size });
      }
    } else if (cur > want) {
      lines.forEach((it, i) => {
        const q = i === 0 ? want : 0;
        if (num(it.quantity) !== q) changes.push({ op: 'change', key: it.key, quantity: q, size });
      });
    }
  });
  return adds.concat(changes);
}

/* ─────────────────────────── browser runtime ─────────────────────────── */
function init() {
  if (window.__kkCompanionBound) return;
  window.__kkCompanionBound = true;
  let cfg = null;
  try { cfg = JSON.parse((document.querySelector('script[data-kk-companion]') || {}).textContent || ''); } catch (e) { cfg = null; }
  const KK = window.KK || {};
  // PDP cards render whenever the block exists (their own countdown hides them at `end`); only the CART runtime is gated
  const onReady = (fn) => { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true }); else fn(); };
  onReady(() => initCards(KK));
  if (!cfg || !Array.isArray(cfg.buy) || !cfg.gift) return;
  const anySize = cfg.sizeMatch === false;
  if (anySize ? !cfg.gift.all : (!cfg.gift.variants || !cfg.buyVariants)) return;
  const tz = cfg.tz || 'Europe/Berlin';
  const end = parseInTz(cfg.end, tz);
  if (isNaN(end) || end <= Date.now()) return;
  let force = false;   // ?kk_companion=now -> sessionStorage flag -> the start gate is skipped (preview verification only)
  try {
    if (/[?&]kk_companion=now(&|$)/.test(window.location.search)) window.sessionStorage.setItem('kk_companion_force', '1');
    force = window.sessionStorage.getItem('kk_companion_force') === '1';
  } catch (e) { /* noop */ }
  const startAt = parseInTz(cfg.start, tz);
  if (!force && !isNaN(startAt) && startAt > Date.now()) return;   // published early: cards may show, the cart stays untouched

  const native = !!(KK.cart && typeof KK.cart.add === 'function' && typeof KK.cart.change === 'function');
  const post = (url, body) => fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
    .then((r) => { if (!r.ok) throw new Error('cart request failed'); return r.json().catch(() => null); });
  const plain = { add: (item) => post('/cart/add.js', { items: [item] }), change: (key, q) => post('/cart/change.js', { id: key, quantity: q }) };
  const loud = native ? { add: (item, o) => KK.cart.add(item, o), change: (key, q, o) => KK.cart.change(key, q, o) } : plain;
  const STORE = 'kk_companion_declined';
  const declined = new Set();
  try { (JSON.parse(window.sessionStorage.getItem(STORE) || '[]') || []).forEach((s) => declined.add(String(s))); } catch (e) { /* private mode */ }
  const persist = () => { try { window.sessionStorage.setItem(STORE, JSON.stringify([...declined])); } catch (e) { /* noop */ } };
  const failed = new Set();   // sizes whose add the server rejected on this page view
  const ownKeys = new Set();  // line keys we changed ourselves -> never read as a customer decline
  let busy = false, primed = false, lastCart = null, failures = 0;

  const sectionsOpt = () => {
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    return drawer ? { sections: [drawer.getAttribute('data-section-id')] } : {};
  };
  // silent = page-load pass: plain endpoints + KK.cart.reconcile() -> the drawer repaints but does not open (no `added`)
  const applyOps = (ops, silent) => {
    const api = silent ? plain : loud;
    const opts = sectionsOpt();
    let mutated = false;
    let p = Promise.resolve(null);
    ops.forEach((o) => {
      if (o.op === 'change') ownKeys.add(o.key);
      p = p.then(() => (o.op === 'add'
        ? api.add({ id: o.id, quantity: o.quantity, properties: o.properties }, opts).catch((e) => { failed.add(o.size); throw e; })
        : api.change(o.key, o.quantity, opts)
      ).then((cart) => { mutated = true; return cart; }));
    });
    return p.then((cart) => ({ cart: cart && Array.isArray(cart.items) ? cart : null, mutated }), (e) => { if (mutated) return { cart: null, mutated }; throw e; });
  };
  const afterSilent = () => {
    if (native && typeof KK.cart.reconcile === 'function') { try { KK.cart.reconcile(); } catch (e) { /* noop */ } return; }
    window.location.reload();
  };
  const run = (cart, silent) => {
    if (busy || !cart || failures >= 3) return;
    busy = true;
    let passes = 0, mutated = false;
    const step = (c) => {
      const ops = planReconcile(c, cfg, new Set([...declined, ...failed]));
      if (!ops.length || passes >= 3) return Promise.resolve(c);
      passes += 1;
      return applyOps(ops, silent).then((r) => {
        mutated = mutated || r.mutated; failures = 0;
        if (r.cart) return step(r.cart);
        // plain endpoints do not return the full cart -> read it once and continue
        return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((x) => x.json()).then(step).catch(() => c);
      });
    };
    step(cart).then((c) => { lastCart = c; }, () => { failures += 1; }).then(() => {
      busy = false;
      if (!mutated) return;
      if (silent) afterSilent();
      else if (!native) window.location.reload();
    });
  };
  const VSTORE = 'kk_companion_variant';
  if (anySize) {
    const ok = (id) => !!(id && cfg.gift.all[String(id)] && cfg.gift.all[String(id)].available);
    let pick = '';
    try { pick = window.sessionStorage.getItem(VSTORE) || ''; } catch (e) { /* noop */ }
    cfg.chosen = ok(pick) ? String(pick) : (ok(cfg.defaultVariant) ? String(cfg.defaultVariant) : '');
    // 2026-09-16 (Jonas): no auto-add - the customer ticks the PDP checkbox (sessionStorage kk_companion_optin) or uses the cart gift bar
    try { cfg.optIn = window.sessionStorage.getItem('kk_companion_optin') === '1'; } catch (e) { cfg.optIn = false; }
    window.addEventListener('kk:companion:optin', (e) => {
      const on = !!(e && e.detail && e.detail.on);
      cfg.optIn = on;
      const c = lastCart || (KK.cart && KK.cart.state);
      if (on) { if (declined.delete('*')) persist(); if (c) run(c, false); return; }
      // unticked: take the gift line out again (only our flagged lines)
      if (!c || !Array.isArray(c.items) || !KK.cart || typeof KK.cart.change !== 'function') return;
      c.items.forEach((it) => { if (isGiftLine(it)) { ownKeys.add(it.key); KK.cart.change(it.key, 0).catch(() => {}); } });
    });
    window.addEventListener('kk:companion:pick', (e) => {
      const id = e && e.detail ? String(e.detail.id || '') : '';
      if (!ok(id) || id === cfg.chosen) return;
      cfg.chosen = id;
      const c = lastCart || (KK.cart && KK.cart.state);
      if (c) run(c, false);
    });
  }
  const declineLine = (line) => { const s = line && isGiftLine(line) && (anySize ? '*' : giftSizeOf(cfg, line.id)); if (s) { declined.add(s); persist(); } };
  const onCartUpdated = (p) => {
    const cart = (p && p.cart) || (KK.cart && KK.cart.state);
    const prev = lastCart || (KK.cart && KK.cart.state) || cart;
    // decline signals are read even while we are busy - only our own line keys are ignored
    if (p && p.changed) {
      if (ownKeys.has(p.changed.key)) ownKeys.delete(p.changed.key);
      else if (p.changed.previous && isGiftLine(p.changed.previous) && num(p.changed.quantity) < num(p.changed.previous.quantity)) declineLine(p.changed.previous);
    }
    if (p && p.updated && p.updated.updates && prev) {
      Object.keys(p.updated.updates).forEach((key) => {
        if (ownKeys.has(key)) { ownKeys.delete(key); return; }
        const line = (prev.items || []).find((it) => it.key === key || String(it.id) === String(key));
        if (line && isGiftLine(line) && num(p.updated.updates[key]) < num(line.quantity)) declineLine(line);
      });
    }
    if (busy) return;   // our own echo (the reconcile loop re-plans on the cart our mutation returned)
    primed = true;
    if (!cart || !Array.isArray(cart.items)) return;
    if (p && p.added && Array.isArray(p.added.items)) {
      p.added.items.forEach((it) => {
        const s = it && (cfg.buy.map(num).indexOf(num(it.product_id)) !== -1) && (anySize ? '*' : cfg.buyVariants[String(it.id)]);
        if (s && declined.delete(s)) persist();
      });
    }
    lastCart = cart;
    run(cart, false);
  };

  const start = () => {
    if (KK.pubsub) KK.pubsub.subscribe('kk:cart:updated', onCartUpdated);
    if (KK.cart && KK.cart.state) { lastCart = KK.cart.state; run(KK.cart.state, true); return; }
    const read = (native && typeof KK.cart.get === 'function')
      ? KK.cart.get()
      : fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    read.then((cart) => { if (!primed) { lastCart = cart; run(cart, true); } }).catch(() => {});
  };
  onReady(start);
}

function initCards(KK) {
  document.querySelectorAll('[data-companion-card]').forEach((card) => {
    let map = null;
    try { map = JSON.parse(card.getAttribute('data-map') || ''); } catch (e) { map = null; }
    const sel = card.querySelector('[data-companion-size]');
    if (map && map.any && sel) {
      // any-size card: remember the pick, tell the cart runtime (same page) to add / swap the gift line
      let pick = '';
      try { pick = window.sessionStorage.getItem('kk_companion_variant') || ''; } catch (e) { /* noop */ }
      if (pick && sel.querySelector('option[value="' + pick.replace(/[^0-9]/g, '') + '"]')) sel.value = pick;
      sel.addEventListener('change', () => {
        try { window.sessionStorage.setItem('kk_companion_variant', sel.value); } catch (e) { /* noop */ }
        window.dispatchEvent(new CustomEvent('kk:companion:pick', { detail: { id: sel.value } }));
      });
      // 2026-09-17 (Jonas): compact card - full price of the chosen size (struck through) and how much is missing to the gift
      const fullEl = card.querySelector('[data-gc-full]');
      const paintFull = () => { if (!fullEl) return; const o = sel.options[sel.selectedIndex]; fullEl.textContent = (o && o.getAttribute('data-full')) || ''; };
      sel.addEventListener('change', paintFull); paintFull();
      const stEl = card.querySelector('[data-gc-status]');
      if (stEl) {
        const min = stEl.getAttribute('data-fx') === '1' ? (parseInt(stEl.getAttribute('data-min'), 10) || 0) : Math.round((parseInt(stEl.getAttribute('data-min'), 10) || 0) * ((window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate)) || 1)), sample = stEl.getAttribute('data-money') || '';
        const prices = {}; (stEl.getAttribute('data-prices') || '').split(',').forEach((p) => { const kv = p.split(':'); if (kv[0]) prices[kv[0]] = parseInt(kv[1], 10) || 0; });
        const fmt = (c) => { const v = (Math.max(0, c) / 100).toFixed(2).replace('.', sample.indexOf('123,45') >= 0 ? ',' : '.'); return sample.replace(/123[.,]45/, v) || v; };
        const paintStatus = () => {
          const sid0 = card.getAttribute('data-section-id') || '', fid = 'product-form-' + sid0, form = document.getElementById(fid);
          const inp = (form && form.querySelector('[name=id]')) || document.querySelector('[data-variant-id-input]');
          let unit = prices[inp ? String(inp.value) : ''] || 0;
          const tile = document.querySelector('[data-set-tile].is-active'); const reg = tile ? Number(tile.getAttribute('data-reg')) || 0 : 0; if (reg > 0) unit = reg;
          const q = document.querySelector('input[type="number"][name="quantity"][form="' + fid + '"]') || (form && form.querySelector('input[type="number"][name="quantity"]'));
          const n = Math.max(1, parseInt(q && q.value, 10) || 1);
          let inCart = 0; const c = KK.cart && KK.cart.state;
          if (c && Array.isArray(c.items)) c.items.forEach((it) => { if (!isGiftLine(it)) inCart += Number(it.original_line_price) || 0; });
          const have = unit * n + inCart, ok = have >= min, t = stEl.querySelector('[data-gc-status-text]'), bar = stEl.querySelector('[data-gc-bar]');
          if (t) t.textContent = ok ? '✓ ' + (stEl.getAttribute('data-tpl-ok') || '') : (stEl.getAttribute('data-tpl-need') || '').replace('{amount}', fmt(min - have));
          if (bar) bar.style.width = Math.min(100, Math.round(have / Math.max(1, min) * 100)) + '%';
          stEl.classList.toggle('is-ok', ok); card.classList.toggle('is-below', !ok);
        };
        let tmr = 0; const later = () => { clearTimeout(tmr); tmr = setTimeout(paintStatus, 60); };
        if (KK.pubsub) { KK.pubsub.subscribe('kk:variant:changed', later); KK.pubsub.subscribe('kk:cart:updated', later); }
        document.addEventListener('click', later, true); document.addEventListener('change', later, true);
        paintStatus(); setTimeout(paintStatus, 800);
      }
      const opt = card.querySelector('[data-companion-optin]');
      // 2026-09-17: the cart runtime only works inside the promo window - before the start the button is locked so it can
      // never show 'selected' while nothing gets added (preview force flag ?kk_companion=now unlocks it).
      let locked = false;
      try {
        const c0 = JSON.parse((document.querySelector('script[data-kk-companion]') || {}).textContent || 'null');
        const forced = window.sessionStorage.getItem('kk_companion_force') === '1' || /[?&]kk_companion=now(&|$)/.test(window.location.search);
        if (c0 && c0.start && !forced) { const st = parseInTz(c0.start, c0.tz || 'Europe/Berlin'); if (!isNaN(st) && st > Date.now()) locked = true; }
      } catch (e) { /* noop */ }
      if (locked) {
        const b0 = card.querySelector('[data-companion-add]');
        if (b0) { b0.disabled = true; b0.classList.add('is-locked'); const t0 = b0.querySelector('[data-companion-add-text]'); if (t0) t0.textContent = b0.getAttribute('data-label-locked') || t0.textContent; }
        try { window.sessionStorage.removeItem('kk_companion_optin'); } catch (e) { /* noop */ }
        return;
      }
      if (opt) {
        try { opt.checked = window.sessionStorage.getItem('kk_companion_optin') === '1'; } catch (e) { /* noop */ }
        const btn = card.querySelector('[data-companion-add]');
        const paint = () => {
          if (!btn) return;
          const t = btn.querySelector('[data-companion-add-text]'), note = card.querySelector('[data-companion-add-note]');
          btn.classList.toggle('is-on', opt.checked); btn.setAttribute('aria-pressed', opt.checked ? 'true' : 'false');
          if (t) t.textContent = (opt.checked ? '✓ ' : '+ ') + (btn.getAttribute(opt.checked ? 'data-label-on' : 'data-label-off') || '');
          if (note) note.hidden = !opt.checked;
          const how = card.querySelector('[data-companion-add-how]'); if (how) how.hidden = opt.checked;
        };
        opt.addEventListener('change', () => {
          try { if (opt.checked) window.sessionStorage.setItem('kk_companion_optin', '1'); else window.sessionStorage.removeItem('kk_companion_optin'); } catch (e) { /* noop */ }
          window.dispatchEvent(new CustomEvent('kk:companion:optin', { detail: { on: opt.checked } }));
          paint();
        });
        if (btn) btn.addEventListener('click', () => { opt.checked = !opt.checked; opt.dispatchEvent(new Event('change', { bubbles: true })); });
        paint();
      }
      return;
    }
    const line = card.querySelector('[data-companion-line]');
    if (!map || !map.sizes || !line) return;
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; n.textContent = text; return n; };
    const note = card.querySelector('[data-companion-note]');
    const render = (variantId) => {
      const size = (map.topper || {})[String(variantId)];
      let s = size && map.sizes[size];
      let noteText = '';
      // sold out in the exact size -> configured substitute size + note (same rule as the cart module)
      if (size && !(s && s.available)) {
        const alt = (map.fallbacks || {})[size];
        const a = alt && map.sizes[alt];
        if (a && a.available) {
          const exact = (map.sizes[size] && map.sizes[size].size_fmt) || size.replace('x', ' x ') + ' cm';
          noteText = String(map.note || '').replace(/\{size\}/g, exact).replace(/\{alt\}/g, a.size_fmt || alt);
          s = a;
        }
      }
      if (note) { note.textContent = noteText; note.hidden = !noteText; }
      line.textContent = '';
      line.classList.toggle('is-soldout', !(s && s.available));
      if (!(s && s.available)) { line.textContent = line.getAttribute('data-soldout') || ''; return; }
      const tpl = line.getAttribute('data-tpl') || 'Matratzenschoner {size} · UVP {uvp} → {price}';
      const parts = tpl.split(/(\{size\}|\{uvp\}|\{price\})/);
      let buf = '';
      const flush = () => { if (buf) line.appendChild(document.createTextNode(buf)); buf = ''; };
      parts.forEach((t, i) => {
        if (t === '{size}') { line.appendChild(el('span', 'companion-line__name', buf + (s.size_fmt || size))); buf = ''; }
        else if (t === '{uvp}') { flush(); line.appendChild(el('s', '', s.uvp_fmt || '')); }
        else if (t === '{price}') { flush(); line.appendChild(el('b', '', s.price_fmt || '')); }
        else if (i === parts.length - 1 && tpl.indexOf('{price}') === -1 && /(→|->)/.test(t)) {
          const k = Math.max(t.lastIndexOf('→'), t.lastIndexOf('->')); const cut = k + (t[k] === '-' ? 2 : 1);
          buf += t.slice(0, cut) + ' '; flush(); line.appendChild(el('b', '', t.slice(cut).trim()));
        } else buf += t;
      });
      flush();
    };
    const current = () => {
      const form = card.closest('form');
      const inp = (form && form.querySelector('[name=id]')) || document.querySelector('[data-variant-id-input]');
      return inp ? inp.value : '';
    };
    render(current());
    const sid = card.getAttribute('data-section-id');
    if (KK.pubsub) KK.pubsub.subscribe('kk:variant:changed', (p) => {
      if (!p || !p.variant || p.variant.id == null || (sid && p.sectionId && p.sectionId !== sid)) return;
      render(p.variant.id);
    });
  });
}

if (typeof window !== 'undefined') init();
